import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { useAuthStore } from '@/core/auth';
import { useStageStore } from '@/core/stores/stage-store';
import DesignerPage from '@/pages/DesignerPage';
import LibraryPage from '@/pages/LibraryPage';
import LoginScreen from '@/ui/LoginScreen';

// Embedded mode = opened by the HitFactor.pro parent app, which supplies the
// JWT via postMessage. The standalone login screen must not block that flow.
function isEmbedded(): boolean {
  const params = new URLSearchParams(window.location.search);
  return Boolean(params.get('stageId')) || window.opener != null || window.self !== window.top;
}

/**
 * Main application component with routing
 * Routes:
 * / → redirect to /designer
 * /designer → DesignerPage (main editor)
 * /library → LibraryPage (placeholder)
 */
export default function App() {
  const isAuthLoading = useAuthStore((s) => s.isLoading);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const guestMode = useAuthStore((s) => s.guestMode);

  // Initialize auth from postMessage and session on mount
  useEffect(() => {
    const auth = useAuthStore.getState();
    auth.initFromPostMessage();
    auth.initFromSession();
  }, []);

  // Parse URL params and auto-load stage if needed
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const matchId = params.get('matchId');
    const stageId = params.get('stageId');

    const stage = useStageStore.getState();
    if (matchId && stageId && !stage.layout) {
      stage.loadFromServer(matchId, stageId).catch((error) => {
        console.error('Failed to load stage from server:', error);
        toast.error('Failed to load stage from server — starting with an empty layout');
      });
    }
  }, []);

  // If auth is still loading, show a simple loading state
  if (isAuthLoading) {
    return (
      <div className="flex items-center justify-center w-screen h-screen bg-background text-foreground">
        <div className="text-center">
          <div className="mb-4 text-lg font-semibold">Loading HitFactor.pro Stage Designer…</div>
          <div className="w-16 h-16 mx-auto border-4 border-muted rounded-full border-t-primary animate-spin" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !guestMode && !isEmbedded()) {
    return (
      <>
        <LoginScreen />
        <Toaster position="bottom-right" richColors closeButton />
      </>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<Navigate to="/designer" replace />} />
        <Route path="/designer" element={<DesignerPage />} />
        <Route path="/library" element={<LibraryPage />} />
        <Route path="*" element={<Navigate to="/designer" replace />} />
      </Routes>
      <Toaster position="bottom-right" richColors closeButton />
    </>
  );
}
